// M07 Bildschirme – Liste (Kacheln/Tabelle, Filter, Mehrfachauswahl → Gruppe/Inhalt zuweisen) inkl. Gruppen (M10, vereinfacht).
import { useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { useMonitoringOverview } from '../api/monitoring';
import { can, qk, useAssignments, useDevices, useGroups, useLocations, useMe, usePlaylists, useSchedules } from '../api/queries';
import type { Device, DeviceGroup } from '../api/types';
import { Icon } from '../components/Icon';
import { AlertBadges, ScreenshotImage } from '../components/Monitoring';
import { PlaylistThumb } from '../components/Preview';
import { useToast } from '../components/toast';
import { Dialog, Empty, Field, PageHead, relativeTime, Spinner, StatusPill } from '../components/ui';
import { localeOf } from '../i18n';
import { deviceState, effectiveAssignment } from '../lib';
import { AssignDialog } from './AssignDialog';

const PLATFORM_ICON: Record<string, string> = { android: 'Android', windows: 'Windows', linux: 'Linux', browser: 'Browser' };

export function ScreensPage() {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const { data: me } = useMe();
  const devices = useDevices();
  const { data: locations = [] } = useLocations();
  const { data: groups = [] } = useGroups();
  const { data: assignments = [] } = useAssignments();
  const { data: playlists = [] } = usePlaylists();
  const { data: schedules = [] } = useSchedules();
  const { data: overview } = useMonitoringOverview(can(me, 'monitoring.read'));
  const mon = useMemo(() => new Map((overview?.items ?? []).map((i) => [i.deviceId, i])), [overview]);
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [view, setView] = useState<'tiles' | 'table'>(() => (localStorage.getItem('dq.screens.view') as 'tiles' | 'table' | null) ?? 'tiles');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dialog, setDialog] = useState<'group' | 'content' | 'groups' | null>(null);

  const q = params.get('q') ?? '';
  const status = params.get('status') ?? '';
  const loc = params.get('location') ?? '';
  const grp = params.get('group') ?? '';
  const platform = params.get('platform') ?? '';
  const set = (k: string, v: string) => {
    const n = new URLSearchParams(params);
    if (v) n.set(k, v);
    else n.delete(k);
    setParams(n, { replace: true });
  };
  const setViewP = (v: 'tiles' | 'table') => {
    setView(v);
    try {
      localStorage.setItem('dq.screens.view', v);
    } catch {
      /* egal */
    }
  };

  const rows = useMemo(() => (devices.data ?? []).filter((d) => {
    if (q && !d.name.toLowerCase().includes(q.toLowerCase())) return false;
    const st = deviceState(d);
    if (status && st !== status) return false;
    if (loc && d.locationId !== loc) return false;
    if (grp && !groups.find((g) => g.id === grp)?.deviceIds.includes(d.id)) return false;
    if (platform && d.platform !== platform) return false;
    return true;
  }).sort((a, b) => a.name.localeCompare(b.name)), [devices.data, q, status, loc, grp, platform, groups]);

  const playsLabel = (d: Device) => {
    const eff = effectiveAssignment(d, assignments, groups);
    if (!eff) return { text: '–', playlistId: null as string | null };
    const a = eff.assignment;
    const name = a.playlistId ? playlists.find((p) => p.id === a.playlistId)?.name : schedules.find((s) => s.id === a.scheduleId)?.name;
    const viaText = eff.via === 'device' ? '' : ` · ${t(`assign.via.${eff.via}`)}`;
    return { text: `${name ?? '–'}${a.scheduleId ? ` (${t('schedules.one')})` : ''}${viaText}`, playlistId: a.playlistId };
  };
  const locName = (id: string | null) => locations.find((l) => l.id === id)?.name ?? '–';
  const groupNames = (d: Device) => groups.filter((g) => g.deviceIds.includes(d.id)).map((g) => g.name).join(', ');
  const toggle = (id: string) => setSelected((s) => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    return n;
  });

  const canPair = can(me, 'devices.pair');
  const actions = (
    <div className="row">
      {can(me, 'groups.manage') && <button className="btn" onClick={() => setDialog('groups')}><Icon name="grid" />{t('screens.groups')}</button>}
      {canPair && <Link className="btn primary" to="/screens/add"><Icon name="plus" />{t('screens.add')}</Link>}
    </div>
  );

  return (
    <>
      <PageHead title={t('nav.screens')} sub={t('screens.sub')} actions={actions} />
      <div className="toolbar">
        <input type="search" placeholder={t('common.search')} aria-label={t('common.search')} value={q} onChange={(e) => set('q', e.target.value)} />
        <select aria-label={t('screens.filterStatus')} value={status} onChange={(e) => set('status', e.target.value)}>
          <option value="">{t('screens.allStatus')}</option>
          {['online', 'offline', 'unknown', 'revoked'].map((s) => <option key={s} value={s}>{t(`status.${s}`)}</option>)}
        </select>
        <select aria-label={t('screens.filterLocation')} value={loc} onChange={(e) => set('location', e.target.value)}>
          <option value="">{t('screens.allLocations')}</option>
          {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        {groups.length > 0 && (
          <select aria-label={t('screens.filterGroup')} value={grp} onChange={(e) => set('group', e.target.value)}>
            <option value="">{t('screens.allGroups')}</option>
            {groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
          </select>
        )}
        <select aria-label={t('screens.filterPlatform')} value={platform} onChange={(e) => set('platform', e.target.value)}>
          <option value="">{t('screens.allPlatforms')}</option>
          {Object.entries(PLATFORM_ICON).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <div className="seg" role="group" aria-label={t('screens.view')}>
          <button className={view === 'tiles' ? 'on' : ''} aria-pressed={view === 'tiles'} onClick={() => setViewP('tiles')} title={t('screens.tiles')}><Icon name="grid" /></button>
          <button className={view === 'table' ? 'on' : ''} aria-pressed={view === 'table'} onClick={() => setViewP('table')} title={t('screens.table')}><Icon name="rows" /></button>
        </div>
      </div>
      {selected.size > 0 && (
        <div className="card flat row" style={{ marginBottom: 14, padding: 12 }}>
          <b>{t('screens.selected', { count: selected.size })}</b>
          <span className="grow" />
          {can(me, 'groups.manage') && groups.length > 0 && <button className="btn sm" onClick={() => setDialog('group')}>{t('screens.assignGroup')}</button>}
          {can(me, 'assignments.manage') && <button className="btn sm primary" onClick={() => setDialog('content')}>{t('screens.assignContent')}</button>}
          <button className="btn sm ghost" onClick={() => setSelected(new Set())}>{t('common.clear')}</button>
        </div>
      )}
      {devices.isLoading ? <Spinner /> : !devices.data?.length ? (
        <div className="card"><Empty icon="screen" title={t('screens.empty')} text={t('screens.emptyText')}
          action={canPair ? <Link className="btn primary" to="/screens/add"><Icon name="plus" />{t('screens.add')}</Link> : undefined} /></div>
      ) : !rows.length ? <div className="card muted">{t('common.noMatches')}</div> : view === 'tiles' ? (
        <div className="grid-cards">
          {rows.map((d) => {
            const plays = playsLabel(d);
            const m = mon.get(d.id);
            return (
              <div key={d.id} className="card screen-card" style={{ padding: 14 }}>
                <Link to={`/screens/${d.id}`} className="tv" aria-label={d.name}>
                  {m?.latestScreenshot ? <ScreenshotImage shot={m.latestScreenshot} compact />
                    : plays.playlistId ? <PlaylistThumb playlistId={plays.playlistId} /> : <span className="small">{t('screens.noContent')}</span>}
                  <AlertBadges item={m} />
                </Link>
                <div className="row" style={{ gap: 8 }}>
                  <input type="checkbox" aria-label={t('screens.select', { name: d.name })} checked={selected.has(d.id)} onChange={() => toggle(d.id)} />
                  <Link to={`/screens/${d.id}`} className="grow" style={{ fontWeight: 650, color: 'inherit', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</Link>
                  <StatusPill state={deviceState(d)} />
                </div>
                <div className="muted small">{locName(d.locationId)} · {PLATFORM_ICON[d.platform]} · {d.lastSeenAt ? t('screens.seen', { when: relativeTime(d.lastSeenAt, locale) }) : t('screens.never')}</div>
                <div className="small" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}><Icon name="play" /> {plays.text}</div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="card scroll" style={{ padding: 10 }}>
          <table className="list">
            <thead><tr>
              <th><input type="checkbox" aria-label={t('screens.selectAll')} checked={selected.size === rows.length} onChange={(e) => setSelected(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())} /></th>
              <th>{t('screens.colStatus')}</th><th>{t('common.name')}</th><th>{t('screens.colLocation')}</th><th>{t('screens.colGroups')}</th>
              <th>{t('screens.colPlays')}</th><th>{t('screens.colSync')}</th><th>{t('screens.colPlatform')}</th><th>{t('screens.colSeen')}</th>
            </tr></thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d.id} className="click" onClick={(e) => (e.target as HTMLElement).tagName !== 'INPUT' && navigate(`/screens/${d.id}`)}>
                  <td><input type="checkbox" aria-label={t('screens.select', { name: d.name })} checked={selected.has(d.id)} onChange={() => toggle(d.id)} /></td>
                  <td><span className="row" style={{ gap: 6 }}><StatusPill state={deviceState(d)} /><AlertBadges item={mon.get(d.id)} /></span></td>
                  <td><b>{d.name}</b></td>
                  <td>{locName(d.locationId)}</td>
                  <td className="muted">{groupNames(d) || '–'}</td>
                  <td>{playsLabel(d).text}</td>
                  <td>{d.status === 'revoked' ? '–' : d.connectivity === 'online' ? '✓' : '–'}</td>
                  <td>{PLATFORM_ICON[d.platform]}</td>
                  <td className="muted">{relativeTime(d.lastSeenAt, locale)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {dialog === 'content' && <AssignDialog targets={[...selected].map((id) => ({ targetType: 'device' as const, targetId: id }))} onClose={() => { setDialog(null); setSelected(new Set()); }} />}
      {dialog === 'group' && <AddToGroupDialog deviceIds={[...selected]} groups={groups} onClose={() => { setDialog(null); setSelected(new Set()); }} />}
      {dialog === 'groups' && <GroupsDialog groups={groups} devices={devices.data ?? []} onClose={() => setDialog(null)} />}
    </>
  );
}

function AddToGroupDialog({ deviceIds, groups, onClose }: { deviceIds: string[]; groups: DeviceGroup[]; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [gid, setGid] = useState(groups[0]?.id ?? '');
  const save = async () => {
    const g = groups.find((x) => x.id === gid);
    if (!g) return;
    try {
      await api.put(`/device-groups/${g.id}/members`, { deviceIds: [...new Set([...g.deviceIds, ...deviceIds])] });
      await qc.invalidateQueries({ queryKey: qk.groups });
      toast.show(t('common.saved'));
      onClose();
    } catch (e) {
      toast.error(e);
    }
  };
  return (
    <Dialog title={t('screens.assignGroup')} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>{t('common.cancel')}</button>
      <button className="btn primary" onClick={save} disabled={!gid}>{t('common.save')}</button>
    </>}>
      <Field label={t('screens.group')}>
        <select value={gid} onChange={(e) => setGid(e.target.value)}>{groups.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}</select>
      </Field>
    </Dialog>
  );
}

/** M10 Gruppen (vereinfacht): anlegen, umbenennen, löschen, Mitglieder per Mehrfachauswahl. */
function GroupsDialog({ groups, devices, onClose }: { groups: DeviceGroup[]; devices: Device[]; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState('');
  const [edit, setEdit] = useState<DeviceGroup | null>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: qk.groups });
  const run = async (fn: () => Promise<unknown>) => {
    try {
      await fn();
      await refresh();
    } catch (e) {
      toast.error(e);
    }
  };
  return (
    <Dialog title={t('screens.groups')} onClose={onClose} actions={<button className="btn" onClick={onClose}>{t('common.close')}</button>}>
      <form className="row" onSubmit={(e) => { e.preventDefault(); void run(() => api.post('/device-groups', { name })).then(() => setName('')); }}>
        <input className="grow" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('screens.newGroup')} aria-label={t('screens.newGroup')} />
        <button className="btn" disabled={!name.trim()}><Icon name="plus" />{t('common.add')}</button>
      </form>
      {groups.length ? (
        <ul className="list-plain">
          {groups.map((g) => (
            <li key={g.id}>
              <b className="grow">{g.name}</b>
              <span className="muted small">{t('screens.groupCount', { count: g.deviceIds.length })}</span>
              <button className="btn sm" onClick={() => setEdit(g)}>{t('screens.members')}</button>
              <button className="btn sm ghost icon" aria-label={t('common.rename')} onClick={() => {
                const n = window.prompt(t('common.rename'), g.name);
                if (n && n !== g.name) void run(() => api.patch(`/device-groups/${g.id}`, { name: n }, { ifMatch: g.rowVersion }));
              }}><Icon name="edit" /></button>
              <button className="btn sm ghost icon danger" aria-label={t('common.delete')} onClick={() => {
                if (window.confirm(t('screens.deleteGroup', { name: g.name }))) void run(() => api.del(`/device-groups/${g.id}`, { ifMatch: g.rowVersion }));
              }}><Icon name="trash" /></button>
            </li>
          ))}
        </ul>
      ) : <div className="muted">{t('screens.noGroups')}</div>}
      {edit && (
        <div className="card flat col">
          <b>{t('screens.membersOf', { name: edit.name })}</b>
          <div className="col" style={{ gap: 6, maxHeight: 240, overflow: 'auto' }}>
            {devices.map((d) => (
              <label key={d.id} className="check"><input type="checkbox" checked={edit.deviceIds.includes(d.id)}
                onChange={(e) => setEdit({ ...edit, deviceIds: e.target.checked ? [...edit.deviceIds, d.id] : edit.deviceIds.filter((x) => x !== d.id) })} />{d.name}</label>
            ))}
          </div>
          <div className="row"><span className="grow" />
            <button className="btn ghost" onClick={() => setEdit(null)}>{t('common.cancel')}</button>
            <button className="btn primary" onClick={() => void run(() => api.put(`/device-groups/${edit.id}/members`, { deviceIds: edit.deviceIds })).then(() => setEdit(null))}>{t('common.save')}</button>
          </div>
        </div>
      )}
    </Dialog>
  );
}
